import React, { useEffect, useState, Children } from 'react';
import { Header } from '../components/Header';
import { VideoPlayer } from '../components/VideoPlayer';
import { StatsSection } from '../components/StatsSection';
import { HeroHighlight, Highlight } from '../components/ui/HeroHighlight';
import { CalendlyEmbed } from '../components/CalendlyEmbed';
import { FAQSection } from '../components/FAQSection';
import { Footer } from '../components/Footer';
import { BookCallButton } from '../components/ui/BookCallButton';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
export function OnboardingPage() {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const scrollToCalendly = () => {
    const element = document.getElementById('calendly');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleNextStep = () => {
    setIsDialogOpen(true);
  };
  const handleConfirm = () => {
    setIsDialogOpen(false);
    navigate('/culture');
  };
  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100,
        duration: 0.4
      }
    }
  };
  return <div className="w-full min-h-screen" data-id="element-240">
      <HeroHighlight data-id="element-241">
        <Header data-id="element-242" />
        <motion.main className="max-w-6xl mx-auto px-4" initial="hidden" animate="visible" variants={containerVariants} data-id="element-243">
          <motion.div id="headline" variants={itemVariants} data-id="element-244">
            <div className="flex justify-center mb-4 sm:mb-6 items-center" data-id="element-245">
              <div className="flex items-center" data-id="element-246">
                <motion.button onClick={() => navigate('/')} className="mr-2 sm:mr-3 h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-white hover:bg-white/10 transition-colors" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} data-id="element-247">
                  <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-248" />
                </motion.button>
                <div className="inline-flex items-center px-3 sm:px-6 py-1.5 sm:py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm" data-id="element-249">
                  <span className="text-xs sm:text-sm md:text-base font-medium text-white" data-id="element-250">
                    STEP
                  </span>
                  <div className="inline-flex items-center justify-center w-5 h-5 sm:w-7 sm:h-7 mx-1 sm:mx-2 rounded-full bg-gradient-to-r from-blue-400 to-indigo-600" data-id="element-251">
                    <span className="text-xs sm:text-base font-bold text-white" data-id="element-252">
                      2
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm md:text-base font-medium text-white" data-id="element-253">
                    OF 5
                  </span>
                </div>
                <motion.button onClick={handleNextStep} className="ml-2 sm:ml-3 h-8 w-8 sm:h-9 sm:w-9 flex items-center justify-center rounded-full bg-white/5 backdrop-blur-sm border border-white/10 text-white hover:bg-white/10 transition-colors" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} data-id="element-254">
                  <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-255" />
                </motion.button>
              </div>
            </div>
            <h1 className="text-center text-2xl sm:text-4xl md:text-5xl font-bold mb-6 sm:mb-8 max-w-4xl mx-auto leading-[1.4] sm:leading-[1.3] text-white" data-id="element-256">
              Watch the video below before filling out your{' '}
              <Highlight data-id="element-257">onboarding form</Highlight>
            </h1>
            <div className="mb-10 sm:mb-12" data-id="element-258">
              <p className="text-center text-base sm:text-xl mb-4 sm:mb-6 text-white" data-id="element-259">
                <strong data-id="element-260">
                  Tap play on the video below (Watch at 1.5x speed for faster
                  efficiency)
                </strong>
              </p>
              <VideoPlayer mediaId="esnmjanho3" data-id="element-261" />
            </div>
          </motion.div>
          <motion.div id="calendly" className="text-center mb-6" variants={itemVariants} data-id="element-262">
            <p className="text-center text-xl font-semibold text-white mb-6" data-id="element-263">
              Step 1: Fill out Your Onboarding Form
            </p>
            <div className="flex justify-center mb-8" data-id="element-264">
              <motion.button onClick={() => window.open('https://applicationflow.typeform.com/newpartnerform', '_blank')} className="py-4 px-10 text-xl sm:text-2xl transform hover:scale-105 bg-gradient-to-r from-blue-400/90 to-indigo-600/90 hover:from-blue-400 hover:to-indigo-600 text-white font-bold rounded-xl transition-all duration-300 shadow-xl" whileHover={{
              scale: 1.05,
              boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5)'
            }} whileTap={{
              scale: 0.98
            }} initial={{
              opacity: 1,
              scale: 1
            }} data-id="element-265">
                Begin Onboarding
              </motion.button>
            </div>
            <p className="text-center text-xl font-semibold text-white mb-6" data-id="element-266">
              Step 2: Click The Button Below To Continue
            </p>
            <div className="flex justify-center mb-8" data-id="element-267">
              <div className="w-full max-w-md" data-id="element-268">
                <BookCallButton onClick={handleNextStep} className="py-4 px-10 text-xl sm:text-2xl" data-id="element-269" />
              </div>
            </div>
          </motion.div>
          <motion.div variants={itemVariants} data-id="element-270">
            <CalendlyEmbed data-id="element-271" />
          </motion.div>
          <motion.div id="stats" variants={itemVariants} data-id="element-272">
            <StatsSection data-id="element-273" />
          </motion.div>
          <motion.div id="faq" variants={itemVariants} data-id="element-274">
            <FAQSection data-id="element-275" />
          </motion.div>
          <Footer data-id="element-276" />
        </motion.main>
      </HeroHighlight>
      <ConfirmationDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} onConfirm={handleConfirm} title="Did you fully complete this step?" message="You need to complete this step before proceeding to the next one." data-id="element-277" />
    </div>;
}